import { useState, useContext } from "react";
import styled from "@emotion/styled";
import { SimpleRegistrationsContext } from "pages/SimpleRegistrationsPage";
import { Box as MuiBox } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { phoneNumberFormatHyphen } from "utils/phoneNumber";
import { getBirthday } from "utils/birthdayUtil";
import { TermsModal } from "modals/TermsModal";

const Box = styled(MuiBox)`
  &.container-landscape {
    padding: 0;
  }

  &.container-portrait {
    padding: 3%;
  }

  &.title-landscape {
    font-size: 3vw;
    font-weight: bold;
  }

  &.title-portrait {
    font-size: 4vw;
    font-weight: bold;
  }

  &.info-box {
    border: solid 1px #dee2ec;
    background: #f3f8ff;
    border-radius: 4px;
    padding: 2%;
    font-size: 3vw;
    display: flex;
    gap: 3%;
    font-weight: bold;
    margin: 3% 0;
  }

  &.info-box-landscape {
    gap: 80px;
  }

  &.info-box-portrait {
    gap: 30px;
    align-items: center;
  }

  &.name {
    min-width: 120px;
    width: 25%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &.sex {
    width: 80px;
  }

  &.birthday {
    width: 20%;
    min-width: 180px;
  }

  &.phone-landscape {
    width: 25%;
    min-width: 250px;
    flex-grow: 1;
  }

  &.phone-portrait {
    width: 25%;
    min-width: 250px;
    flex-grow: 1;
  }

  &.checkbox-container {
    margin-bottom: 1%;
  }

  &.terms-row {
    display: flex;
    justify-content: space-between;
  }
`;

const CustomCheckbox = styled(Checkbox)`
  padding: 0;
  margin: 0;

  &.checkbox-landscape svg {
    font-size: 2vw;
  }

  &.checkbox-portrait svg {
    font-size: 2.5vw;
  }
`;

const Span = styled.span`
  font-weight: 800;
`;

interface LabelProps {
  fontWeight?: string;
}

const Label = styled(FormControlLabel)<LabelProps>`
  margin: 0 !important;
  gap: 30px;

  &.label-landscape .MuiFormControlLabel-label {
    font-size: 2vw;
  }

  &.label-portrait .MuiFormControlLabel-label {
    font-size: 2.5vw;
  }

  & .MuiFormControlLabel-label {
    font-weight: ${({ fontWeight }) => fontWeight || "normal"};
  }
`;

const TermsLink = styled.span`
  text-decoration: underline;
  cursor: pointer;

  &.link-landscape {
    font-size: 2vw;
  }

  &.link-portrait {
    font-size: 2.5vw;
  }
`;

interface SimpleRegistrationsContextType {
  isLandscape: boolean;
  name: string;
  birthday: string;
  phoneNumber: string;
  smsEnabled: boolean;
  setSmsEnabled: (enabled: boolean) => void;
  eventSmsEnabled: boolean;
  setEventSmsEnabled: (enabled: boolean) => void;
}

export const InfoConfirm = () => {
  const {
    isLandscape,
    name,
    birthday,
    phoneNumber,
    smsEnabled,
    setSmsEnabled,
    eventSmsEnabled,
    setEventSmsEnabled,
  } = useContext(SimpleRegistrationsContext) as SimpleRegistrationsContextType;
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const [selectedCheckbox, setSelectedCheckbox] = useState<null | string>(null);

  const getSex = (): string => {
    const ssnHead = birthday.charAt(birthday.length - 1);
    if (ssnHead === "2" || ssnHead === "4") {
      return "여";
    } else if (ssnHead === "1" || ssnHead === "3") {
      return "남";
    } else {
      return "";
    }
  };

  const layoutMode = isLandscape ? "landscape" : "portrait";

  return (
    <>
      {openTermsModal && (
        <TermsModal
          open={openTermsModal}
          onClose={(checked: boolean) => {
            setOpenTermsModal(false);
            setSelectedCheckbox(null);
            setSmsEnabled(!smsEnabled ? checked : smsEnabled);
            if (selectedCheckbox === "all") {
              setEventSmsEnabled(checked);
            }
          }}
        />
      )}
      <Box className={`container-${layoutMode}`}>
        <Box className={`title-${layoutMode}`}>
          입력한 <Span>정보 확인 후 약관에 동의</Span>해주세요.
        </Box>
        <Box className={`info-box info-box-${layoutMode}`}>
          <Box className="name">{name}</Box>
          <Box className="sex">{getSex()}</Box>
          <Box className="birthday">{getBirthday(birthday)}</Box>
          <Box className={`phone-${layoutMode}`}>
            {phoneNumberFormatHyphen(phoneNumber)}
          </Box>
        </Box>
        <Box>
          <Box className="checkbox-container">
            <Label
              className={`label-${layoutMode}`}
              fontWeight="bold"
              control={
                <CustomCheckbox
                  checked={smsEnabled && eventSmsEnabled}
                  className={`checkbox-${layoutMode}`}
                />
              }
              label="약관 전체동의"
              onChange={(_, checked) => {
                if (checked && !smsEnabled) {
                  setOpenTermsModal(true);
                  setSelectedCheckbox("all");
                } else {
                  setSmsEnabled(checked);
                  setEventSmsEnabled(checked);
                }
              }}
            />
          </Box>
          <Box className="terms-row">
            <Label
              className={`label-${layoutMode}`}
              control={
                <CustomCheckbox
                  checked={smsEnabled}
                  className={`checkbox-${layoutMode}`}
                />
              }
              label="[필수] 개인정보 수집 및 활용에 대한 처리방침"
              onChange={(_, checked) => {
                if (checked) {
                  setSelectedCheckbox("smsEnabled");
                  setOpenTermsModal(true);
                } else {
                  setSmsEnabled(false);
                }
              }}
            />
            <TermsLink
              className={`link-${layoutMode}`}
              onClick={() => {
                setSelectedCheckbox("smsEnabled");
                setOpenTermsModal(true);
              }}
            >
              전문보기
            </TermsLink>
          </Box>
          <Label
            className={`label-${layoutMode}`}
            control={
              <CustomCheckbox
                checked={eventSmsEnabled}
                className={`checkbox-${layoutMode}`}
              />
            }
            label="[선택] 이벤트 및 광고, 혜택알림 수신"
            onChange={(_, checked) => {
              setEventSmsEnabled(checked);
            }}
          />
        </Box>
      </Box>
    </>
  );
};
